import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNodeConfigBase } from './node-config-base';
import {
  ACTIVITY_TYPE_OPTIONS,
  CONDITION_SOURCE_OPTIONS,
  CRM_OBJECT_TYPE_OPTIONS
} from './config-options';
import { EntitySelectComponent, EntitySelectPage } from '../../../../../../../shared/components/entity-select/entity-select.component';
import { SegmentService } from '../../../../../../segments/services/segment-api.service';
import { Segment } from '../../../../../../segments/models/segment.model';
import { WorkflowPropertySelectComponent } from './property-select.component';
import { buildPropertyOptions } from '../../../models/workflow-variable-catalog';
import { branchKeyLabel } from '../../../models/node-catalog';
import { WorkflowObjectType } from '../../../../models/workflow.model';
import {
  WorkflowConditionValueComponent,
  WorkflowConditionValueParts,
  readConditionValueParts
} from './condition-value.component';

interface IfElseRow extends WorkflowConditionValueParts { source: string; property: string; operator: string; }
interface MultiBranchRow extends WorkflowConditionValueParts { branchKey: string; operator: string; }
interface AbSplitRow { branchKey: string; percentage: number; }

/** Comparación vacía de una fila recién añadida (mismas claves que lee el backend). */
const EMPTY_VALUE_PARTS: WorkflowConditionValueParts = { value: '', from: null, to: null, values: null };

/**
 * Origen por defecto de una fila de IF_ELSE: la propiedad del REGISTRO inscrito.
 *
 * Antes era `PAYLOAD`, y esa combinación no podía funcionar: el selector de propiedades ofrece
 * las claves del catálogo (`email`, `firstname`, `contactCycleName`) mientras que el payload del
 * disparador trae los nombres de la API (`mail`, `fullname`). Nunca casaban, así que la condición
 * evaluaba SIEMPRE falso, sin error de validación ni aviso, y todos los registros salían por la
 * rama "No". `OBJECT` lee el snapshot del registro, que es lo que inyecta el motor al evaluar.
 */
const DEFAULT_CONDITION_SOURCE = 'OBJECT';

/**
 * CONDITION — divide el flujo. Subtipos: IF_ELSE, PROPERTY_CONDITION, MULTI_BRANCH, AB_SPLIT,
 * SEGMENT_MEMBERSHIP_CONDITION, ASSOCIATED_RECORD_CONDITION, ACTIVITY_HISTORY_CONDITION.
 *
 * Las ramas de MULTI_BRANCH/AB_SPLIT son PUERTOS del canvas (gotcha §6): editar el array de
 * ramas cambia los puertos; las aristas que colgaban de un puerto eliminado quedan huérfanas
 * y el canvas las conserva para que el usuario decida borrarlas.
 */
@Component({
  selector: 'app-wf-condition-config',
  standalone: true,
  imports: [
    CommonModule,
    SelectValueSyncDirective,
    EntitySelectComponent,
    WorkflowPropertySelectComponent,
    WorkflowConditionValueComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('IF_ELSE') {
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Condiciones</label>
            <select class="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                    [value]="str('logic', 'AND')" [disabled]="readOnly()" (change)="patchConfig({ logic: inputValue($event) })">
              <option value="AND">Todas (Y)</option>
              <option value="OR">Alguna (O)</option>
            </select>
          </div>
          @for (row of ifElseRows(); track $index) {
            <div class="space-y-2 rounded-lg border border-gray-200 p-2.5 dark:border-gray-800">
              <div class="flex items-center gap-2">
                <select class="w-36 shrink-0 rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                        [value]="row.source" [disabled]="readOnly()"
                        title="De dónde se lee el valor a comparar. Para las propiedades del selector de al lado, usa «Propiedad del registro»."
                        (change)="updateIfElse($index, { source: inputValue($event) })">
                  <!-- Vacío = el motor busca en todas las fuentes (registro, payload, paso anterior).
                       Sin esta opción, un origen sin definir se mostraba como PAYLOAD. -->
                  <option value="">Automático</option>
                  @for (s of sourceOptions; track s.value) { <option [value]="s.value">{{ s.label }}</option> }
                </select>
                <div class="flex-1 min-w-0">
                  <app-wf-property-select
                    [value]="row.property" [options]="propertyOptions()" [disabled]="readOnly()"
                    placeholder="propiedad"
                    (valueChange)="updateIfElse($index, { property: $event })"></app-wf-property-select>
                </div>
                @if (ifElseRows().length > 1) {
                  <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                          [disabled]="readOnly()" (click)="removeIfElse($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
                }
              </div>
              <select class="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                      [value]="row.operator" [disabled]="readOnly()" (change)="updateIfElse($index, { operator: inputValue($event) })">
                @for (o of operatorOptionsFor(propertyOptions(), row.property, row.operator); track o.value) {
                  <option [value]="o.value">{{ o.label }}</option>
                }
              </select>
              <app-wf-condition-value
                [operator]="row.operator" [value]="row.value" [from]="row.from" [to]="row.to" [values]="row.values"
                [property]="row.property" [options]="propertyOptions()"
                [disabled]="readOnly()" (patch)="updateIfElse($index, $event)" />
            </div>
          }
          <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                  [disabled]="readOnly()" (click)="addIfElse()">+ Añadir condición</button>
          <p [class]="hintClass">Ramas: "Sí" (se cumple) y "No" (no se cumple). Conéctalas en el lienzo.</p>
        </div>
      }

      @case ('PROPERTY_CONDITION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Propiedad</label>
            <app-wf-property-select
              [value]="str('property')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchConfig({ property: $event })"></app-wf-property-select>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Operador</label>
              <select [class]="fieldClass" [value]="str('operator', 'EQUALS')" [disabled]="readOnly()"
                      (change)="patchConfig({ operator: inputValue($event) })">
                @for (o of operatorOptionsFor(propertyOptions(), str('property'), str('operator', 'EQUALS')); track o.value) {
                  <option [value]="o.value">{{ o.label }}</option>
                }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Valor</label>
              <app-wf-condition-value
                [operator]="str('operator', 'EQUALS')" [value]="config()['value']" [from]="config()['from']"
                [to]="config()['to']" [values]="config()['values']" [disabled]="readOnly()"
                [property]="str('property')" [options]="propertyOptions()"
                (patch)="patchConfig($event)" />
            </div>
          </div>
        </div>
      }

      @case ('MULTI_BRANCH') {
        <div class="space-y-3">
          <div>
            <label [class]="labelClass">Propiedad de evaluación</label>
            <app-wf-property-select
              [value]="str('property')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchConfig({ property: $event })"></app-wf-property-select>
          </div>
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Ramas</label>
          @for (row of mbRows(); track $index) {
            <div class="space-y-2 rounded-lg border border-gray-200 p-2.5 dark:border-gray-800">
              <div class="flex items-center gap-2">
                <input type="text" class="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                       [value]="row.branchKey" [disabled]="readOnly()" placeholder="rama"
                       (input)="updateMb($index, { branchKey: inputValue($event) })" />
                <select class="min-w-0 flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                        [value]="row.operator" [disabled]="readOnly()" (change)="updateMb($index, { operator: inputValue($event) })">
                  @for (o of operatorOptionsFor(propertyOptions(), str('property'), row.operator); track o.value) {
                    <option [value]="o.value">{{ o.label }}</option>
                  }
                </select>
                <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                        [disabled]="readOnly()" (click)="removeMb($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
              </div>
              <app-wf-condition-value
                [operator]="row.operator" [value]="row.value" [from]="row.from" [to]="row.to" [values]="row.values"
                [property]="str('property')" [options]="propertyOptions()"
                [disabled]="readOnly()" (patch)="updateMb($index, $event)" />
            </div>
          }
          <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                  [disabled]="readOnly()" (click)="addMb()">+ Añadir rama</button>
          <!--
            INC_CRM_020 (8) inc27: el campo "Rama por defecto (fallback)" era un texto libre cuyo
            valor es la clave del puerto ("DEFAULT"). Renombrarlo no aporta nada —el puerto solo
            existe para colgar la arista— y era el único texto en inglés del formulario.
          -->
          <p [class]="hintClass">
            Si el valor de la propiedad no coincide con ninguna rama, el flujo continúa por la salida
            "{{ fallbackBranchLabel() }}".
          </p>
        </div>
      }

      @case ('AB_SPLIT') {
        <div class="space-y-3">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Ramas (porcentaje)</label>
          @for (row of abRows(); track $index) {
            <div class="flex items-center gap-2">
              <input type="text" class="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                     [value]="row.branchKey" [disabled]="readOnly()" placeholder="rama"
                     (input)="updateAb($index, { branchKey: inputValue($event) })" />
              <input type="number" min="0" max="100" [class]="fieldClass" [value]="row.percentage" [disabled]="readOnly()"
                     (change)="updateAb($index, { percentage: numberValue($event) })" />
              <span class="text-xs text-gray-400">%</span>
              @if (abRows().length > 1) {
                <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                        [disabled]="readOnly()" (click)="removeAb($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
              }
            </div>
          }
          <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                  [disabled]="readOnly()" (click)="addAb()">+ Añadir rama</button>
          @if (abTotal() !== 100) {
            <p class="text-xs text-warning-500">La suma de porcentajes es {{ abTotal() }}% (debe ser 100%).</p>
          }
        </div>
      }

      @case ('SEGMENT_MEMBERSHIP_CONDITION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Segmento</label>
            <app-entity-select
              [value]="num('segmentId') || null" [displayText]="segmentName()"
              [searchFn]="segmentSearch" [labelFn]="segmentLabel" [disabled]="readOnly()"
              placeholder="Buscar segmento por nombre…"
              (valueChange)="patchConfig({ segmentId: $event })" (selected)="onSegmentSelected($event)"></app-entity-select>
          </div>
          <div>
            <label [class]="labelClass">Pertenencia esperada</label>
            <select [class]="fieldClass" [value]="str('expected', 'IN')" [disabled]="readOnly()"
                    (change)="patchConfig({ expected: inputValue($event) })">
              <option value="IN">Pertenece al segmento</option>
              <option value="NOT_IN">No pertenece</option>
            </select>
          </div>
        </div>
      }

      @case ('ASSOCIATED_RECORD_CONDITION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Objeto asociado</label>
            <select [class]="fieldClass" [value]="str('associatedObjectType', 'CONTACT')" [disabled]="readOnly()"
                    (change)="patchConfig({ associatedObjectType: inputValue($event) })">
              @for (o of crmObjectTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Propiedad</label>
            <app-wf-property-select
              [value]="str('property')" [options]="associatedPropertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchConfig({ property: $event })"></app-wf-property-select>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Operador</label>
              <select [class]="fieldClass" [value]="str('operator', 'EQUALS')" [disabled]="readOnly()"
                      (change)="patchConfig({ operator: inputValue($event) })">
                @for (o of operatorOptionsFor(associatedPropertyOptions(), str('property'), str('operator', 'EQUALS')); track o.value) {
                  <option [value]="o.value">{{ o.label }}</option>
                }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Valor</label>
              <app-wf-condition-value
                [operator]="str('operator', 'EQUALS')" [value]="config()['value']" [from]="config()['from']"
                [to]="config()['to']" [values]="config()['values']" [disabled]="readOnly()"
                [property]="str('property')" [options]="associatedPropertyOptions()"
                (patch)="patchConfig($event)" />
            </div>
          </div>
        </div>
      }

      @case ('ACTIVITY_HISTORY_CONDITION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Tipo de actividad</label>
            <!-- Desplegable y no texto libre: antes había que adivinar la clave exacta que
                 registra el CRM, y escribirla mal daba falso sin avisar. -->
            <select [class]="fieldClass" [value]="str('activityType')" [disabled]="readOnly()"
                    (change)="patchConfig({ activityType: inputValue($event) })">
              <option value="">Elige un tipo…</option>
              @for (a of activityTypeOptions; track a.value) { <option [value]="a.value">{{ a.label }}</option> }
            </select>
            <p [class]="hintClass">
              El historial se lee de la Auditoría del CRM, incluidos los registros relacionados
              (las reuniones o los tickets de un contacto).
            </p>
          </div>
          <div>
            <label [class]="labelClass">Dentro de (días)</label>
            <input type="number" min="1" [class]="fieldClass" [value]="num('withinDays', 30)" [disabled]="readOnly()"
                   (change)="patchConfig({ withinDays: numberValue($event) })" />
          </div>
        </div>
      }

      @default {
        <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Esta condición no tiene campos configurables. Usa el modo avanzado (JSON) si necesitas ajustarla.
        </p>
      }
    }
  `
})
export class WorkflowConditionConfigComponent extends WorkflowNodeConfigBase {
  private readonly segmentService = inject(SegmentService);

  protected readonly sourceOptions = CONDITION_SOURCE_OPTIONS;
  protected readonly crmObjectTypes = CRM_OBJECT_TYPE_OPTIONS;
  protected readonly activityTypeOptions = ACTIVITY_TYPE_OPTIONS;
  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');
  protected readonly segmentName = signal('');

  /** MULTI_BRANCH: nombre legible del puerto por el que sale lo que no encaja en ninguna rama. */
  protected readonly fallbackBranchLabel = computed(() => branchKeyLabel(this.str('fallbackBranchKey', 'DEFAULT')));

  /** Propiedades sugeridas del objeto base del workflow (etiqueta legible + clave). */
  protected readonly propertyOptions = computed(() => buildPropertyOptions(this.editor.workflow()?.objectType ?? null));

  /** ASSOCIATED_RECORD_CONDITION: propiedades del objeto asociado elegido; si no mapea, las del workflow. */
  protected readonly associatedPropertyOptions = computed(() => {
    const opts = buildPropertyOptions(this.str('associatedObjectType', 'CONTACT') as WorkflowObjectType);
    return opts.length ? opts : this.propertyOptions();
  });

  constructor() {
    super();
    effect(() => {
      const id = this.num('segmentId');
      if (!id) {
        this.segmentName.set('');
        return;
      }
      this.segmentService.getSegmentById(id).subscribe({
        next: (s) => this.segmentName.set(s.name),
        error: () => {}
      });
    });
    // Los valores que el panel PINTA por defecto se PERSISTEN. Estos dos subtipos nacen con la
    // config vacía y el backend exige las claves que el formulario ya muestra rellenas
    // ("Es igual a", "Pertenece al segmento"), así que el nodo salía marcado "Configuración
    // incompleta" mostrando un formulario aparentemente completo. Mismo criterio que
    // LIFECYCLE_CHANGED en trigger-config y que CREATE_RECORD en crm-action-config.
    effect(() => {
      const config = this.config();
      if (this.readOnly()) {
        return;
      }
      if (this.subtype() === 'PROPERTY_CONDITION' && config['operator'] == null) {
        this.patchConfig({ operator: 'EQUALS' });
      }
      if (this.subtype() === 'SEGMENT_MEMBERSHIP_CONDITION' && config['expected'] == null) {
        this.patchConfig({ expected: 'IN' });
      }
    });
  }

  // ── IF_ELSE ────────────────────────────────────────────────────────────────
  protected readonly ifElseRows = computed<IfElseRow[]>(() => {
    const raw = this.arr('conditions');
    if (!raw.length) {
      return [{ source: DEFAULT_CONDITION_SOURCE, property: '', operator: 'EQUALS', ...EMPTY_VALUE_PARTS }];
    }
    return raw.map((item) => {
      const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      return {
        source: String(r['source'] ?? DEFAULT_CONDITION_SOURCE),
        property: String(r['property'] ?? ''),
        operator: String(r['operator'] ?? 'EQUALS'),
        ...readConditionValueParts(r)
      };
    });
  });

  protected addIfElse(): void {
    this.patchConfig({
      conditions: [...this.ifElseRows(), { source: DEFAULT_CONDITION_SOURCE, property: '', operator: 'EQUALS', ...EMPTY_VALUE_PARTS }]
    });
  }
  protected updateIfElse(index: number, patch: Record<string, unknown>): void {
    this.patchConfig({ conditions: this.ifElseRows().map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  }
  protected removeIfElse(index: number): void {
    this.patchConfig({ conditions: this.ifElseRows().filter((_, i) => i !== index) });
  }

  // ── MULTI_BRANCH ─────────────────────────────────────────────────────────────
  protected readonly mbRows = computed<MultiBranchRow[]>(() => {
    const raw = this.arr('branches');
    if (!raw.length) {
      return [
        { branchKey: 'A', operator: 'EQUALS', ...EMPTY_VALUE_PARTS },
        { branchKey: 'B', operator: 'EQUALS', ...EMPTY_VALUE_PARTS }
      ];
    }
    return raw.map((item) => {
      const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      return {
        branchKey: String(r['branchKey'] ?? ''),
        operator: String(r['operator'] ?? 'EQUALS'),
        ...readConditionValueParts(r)
      };
    });
  });

  protected addMb(): void {
    this.patchConfig({ branches: [...this.mbRows(), { branchKey: '', operator: 'EQUALS', ...EMPTY_VALUE_PARTS }] });
  }
  protected updateMb(index: number, patch: Record<string, unknown>): void {
    this.patchConfig({ branches: this.mbRows().map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  }
  protected removeMb(index: number): void {
    this.patchConfig({ branches: this.mbRows().filter((_, i) => i !== index) });
  }

  // ── AB_SPLIT ─────────────────────────────────────────────────────────────────
  protected readonly abRows = computed<AbSplitRow[]>(() => {
    const raw = this.arr('branches');
    if (!raw.length) {
      return [{ branchKey: 'A', percentage: 50 }, { branchKey: 'B', percentage: 50 }];
    }
    return raw.map((item) => {
      const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      const pct = Number(r['percentage']);
      return { branchKey: String(r['branchKey'] ?? ''), percentage: Number.isFinite(pct) ? pct : 0 };
    });
  });
  protected readonly abTotal = computed(() => this.abRows().reduce((sum, b) => sum + (b.percentage || 0), 0));

  protected addAb(): void {
    const key = String.fromCharCode(65 + this.abRows().length);
    this.patchConfig({ branches: [...this.abRows(), { branchKey: key, percentage: 0 }] });
  }
  protected updateAb(index: number, patch: Partial<AbSplitRow>): void {
    this.patchConfig({ branches: this.abRows().map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  }
  protected removeAb(index: number): void {
    this.patchConfig({ branches: this.abRows().filter((_, i) => i !== index) });
  }

  // ── Segmento ─────────────────────────────────────────────────────────────────
  protected onSegmentSelected(segment: Segment | null): void {
    this.segmentName.set(segment?.name ?? '');
  }
  segmentSearch = (term: string, page: number): Observable<EntitySelectPage<Segment>> => {
    return this.segmentService.getSegments(page, 10, { searchTerm: term }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as Segment[], totalPages: 0 }))
    );
  };
  segmentLabel = (s: Segment): string => s.name;
}
